import React, { useState } from 'react';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const DataExport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedExport, setSelectedExport] = useState('');
  const [staffId, setStaffId] = useState('');

  const exportOptions = [
    { value: 'staff-info', label: 'Staff Information', endpoint: 'staff-info', filename: 'staff-information' },
    { value: 'job-info', label: 'Job Information', endpoint: 'job-info', filename: 'job-information' },
    { value: 'training', label: 'Training Records', endpoint: 'training', filename: 'training-records' },
    { value: 'benefits', label: 'Benefits Information', endpoint: 'benefits', filename: 'benefits-information' },
    { value: 'previous-work', label: 'Previous Work Experience', endpoint: 'previous-work', filename: 'previous-work-experience' },
    { value: 'computer-skills', label: 'Computer Skills', endpoint: 'computer-skills', filename: 'computer-skills' },
    { value: 'language-skills', label: 'Language Skills', endpoint: 'language-skills', filename: 'language-skills' }
  ];

  const handleExport = async () => {
    if (!selectedExport) {
      setMessage('❌ Please select what you want to export');
      return;
    }

    try {
      setLoading(true);
      setMessage('');

      const selectedOption = exportOptions.find(option => option.value === selectedExport);
      
      // Build URL with optional staff filter
      let url = `/api/export/${selectedOption.endpoint}?format=xlsx`;
      if (staffId.trim()) {
        url += `&staffId=${staffId.trim()}`;
      }
      
      const response = await api.get(url, {
        responseType: 'blob' // Important for file downloads
      });

      // Handle file download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedOption.filename}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      setMessage(`✅ File "${selectedOption.filename}.xlsx" downloaded successfully!`);
      
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 401) {
        setMessage('❌ Authentication failed. Please login again.');
      } else {
        setMessage(`❌ Export failed: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
fr      <div className="container mx-auto px-4 py-8 pt-20">
        <div className="max-w-2xl mx-auto">
          
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Data Export</h1>
            <p className="text-gray-600 mb-4">
              Select what you want to export and download as Excel file.
            </p>
        
            
            {user && user.role !== 'admin' && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-700">
                  📝 <em>You can only export data that you created.</em>
                </p>
              </div>
            )}
          </div>

          {/* Export Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">What would you like to export?</h2>
            
            <div className="space-y-4">
              {/* Export Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Type
                </label>
                <select
                  value={selectedExport}
                  onChange={(e) => setSelectedExport(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select what to export --</option>
                  {exportOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Optional Staff ID Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff ID (Optional)
                </label>
                <input
                  type="text"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  placeholder="Leave empty to export all staff"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter a specific staff ID to export data for just that person
                </p>
              </div>

              {/* Export Button */}
              <div className="pt-4">
                <button
                  onClick={handleExport}
                  disabled={loading || !selectedExport}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>⏳ Exporting...</>
                  ) : (
                    <>📊 Download Excel File</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default DataExport;
